import {
  DOUBLE_ELIMINATION_POSITIONS,
  TournamentPayoutType,
  TournamentType,
} from '@okletsplay/core';
import { isNumber, range } from 'lodash';
import { useMemo } from 'react';
import { multiplyByPercent } from 'shared/utils';

const INDEX_NORMALIZER = 1;
const FIRST_POSITION = 1;
const POW_OF_TWO = 2;
const MIN_ROUND_COUNTS = 2;

const getDoubleEliminationPlaces = (ranksCount: number, teamsCount?: number): number[][] => {
  if (!ranksCount) {
    return [];
  }

  return range(1, ranksCount + INDEX_NORMALIZER)
    .map((round) => {
      if (round < 1) {
        return [1];
      }

      const start = DOUBLE_ELIMINATION_POSITIONS[round - 1];
      let end = DOUBLE_ELIMINATION_POSITIONS[round];

      if (isNumber(teamsCount) && end > teamsCount) {
        end = teamsCount + INDEX_NORMALIZER;
      }

      if (start >= end) {
        return [];
      }

      return range(start, end);
    })
    .filter((places) => places.length);
};

const getAsyncTournamentPlaces = (count: number): number[][] => {
  if (!count) {
    return [];
  }

  return range(FIRST_POSITION, count + INDEX_NORMALIZER).map((position) => [position]);
};

export const useTournamentPlaces = (
  lastWinningPosition: number,
  maxTeamsSize: number,
  isFreeTournament: boolean,
  tournamentType: TournamentType
) => {
  const ranksCount = useMemo(() => {
    switch (tournamentType) {
      case TournamentType.SingleEliminationTournament:
        let ranks = lastWinningPosition === FIRST_POSITION ? FIRST_POSITION : MIN_ROUND_COUNTS;

        if (lastWinningPosition > MIN_ROUND_COUNTS) {
          ranks =
            Math.log(lastWinningPosition - INDEX_NORMALIZER) / Math.log(POW_OF_TWO) +
            MIN_ROUND_COUNTS;
        }

        return ranks;
      case TournamentType.DoubleEliminationTournament:
        const index = DOUBLE_ELIMINATION_POSITIONS.findIndex((pos) => pos == lastWinningPosition);
        return index < 0 ? 0 : index + 1;
      case TournamentType.AsyncTournament:
      default:
        return lastWinningPosition;
    }
  }, [lastWinningPosition, tournamentType]);

  const tournamentPlaces = useMemo(() => {
    if (isFreeTournament) {
      return [range(FIRST_POSITION, maxTeamsSize + INDEX_NORMALIZER || FIRST_POSITION)];
    }

    switch (tournamentType) {
      case TournamentType.SingleEliminationTournament:
        return new Array(Math.round(ranksCount))
          .fill(null)
          .map((team, index) => {
            if (!index) {
              return [index + INDEX_NORMALIZER];
            }

            if (index === FIRST_POSITION && ranksCount >= MIN_ROUND_COUNTS) {
              return [index + INDEX_NORMALIZER];
            }

            const start = Math.pow(POW_OF_TWO, index - INDEX_NORMALIZER) + INDEX_NORMALIZER;
            const end = Math.pow(POW_OF_TWO, index) + INDEX_NORMALIZER;
            return range(start, end);
          })
          ?.filter((places) => places?.length);
      case TournamentType.DoubleEliminationTournament:
        return getDoubleEliminationPlaces(ranksCount);
      case TournamentType.AsyncTournament:
      default:
        return getAsyncTournamentPlaces(lastWinningPosition);
    }
  }, [isFreeTournament, lastWinningPosition, maxTeamsSize, ranksCount, tournamentType]);

  return { rankCounts: ranksCount, tournamentPlaces };
};

export const useActualTournamentPlaces = ({
  activeTeamsCount,
  lastWinningPosition,
  minTeamsCount,
  maxTeamsCount,
  tournamentType,
  isFreeTournament,
  payoutType,
  thresholdPercentage,
}: {
  activeTeamsCount: number;
  lastWinningPosition: number;
  minTeamsCount: number;
  maxTeamsCount: number;
  tournamentType: TournamentType;
  isFreeTournament: boolean;
  payoutType: TournamentPayoutType;
  thresholdPercentage: number;
}) => {
  const teamsCount = Math.max(minTeamsCount, activeTeamsCount);

  const ranksCount = useMemo<number>(() => {
    if (isFreeTournament) {
      return FIRST_POSITION;
    }

    const winningPosition = Math.min(lastWinningPosition, teamsCount);

    if (payoutType === TournamentPayoutType.TopPercent) {
      return multiplyByPercent(winningPosition, thresholdPercentage);
    }

    if (tournamentType === TournamentType.AsyncTournament) {
      return winningPosition;
    }

    switch (true) {
      case winningPosition === FIRST_POSITION:
        return FIRST_POSITION;
      case winningPosition > MIN_ROUND_COUNTS &&
        tournamentType === TournamentType.DoubleEliminationTournament:
        const index = DOUBLE_ELIMINATION_POSITIONS.findIndex((pos) => pos >= winningPosition);
        return index < 0 ? 0 : index + 1;
      case winningPosition > MIN_ROUND_COUNTS &&
        tournamentType === TournamentType.SingleEliminationTournament:
        return (
          Math.log(winningPosition - INDEX_NORMALIZER) / Math.log(POW_OF_TWO) + MIN_ROUND_COUNTS
        );
      default:
        return MIN_ROUND_COUNTS;
    }
  }, [
    isFreeTournament,
    payoutType,
    tournamentType,
    lastWinningPosition,
    teamsCount,
    thresholdPercentage,
  ]);

  const places = useMemo(() => {
    if (isFreeTournament) {
      return [range(FIRST_POSITION, maxTeamsCount + INDEX_NORMALIZER || FIRST_POSITION)];
    }

    switch (tournamentType) {
      case TournamentType.SingleEliminationTournament:
        return new Array(Math.round(ranksCount))
          .fill(null)
          .map((team, index) => {
            if (!index) {
              return [index + INDEX_NORMALIZER];
            }

            if (index === FIRST_POSITION && ranksCount >= MIN_ROUND_COUNTS) {
              return [index + INDEX_NORMALIZER];
            }

            const start = Math.pow(POW_OF_TWO, index - INDEX_NORMALIZER) + INDEX_NORMALIZER;

            if (start > teamsCount) {
              return [];
            }

            let end = Math.pow(POW_OF_TWO, index);

            if (end > teamsCount) {
              end = teamsCount;
            }

            return range(start, end + INDEX_NORMALIZER);
          })
          .filter((tournamentPlaces) => tournamentPlaces.length);
      case TournamentType.DoubleEliminationTournament:
        return getDoubleEliminationPlaces(ranksCount, teamsCount);
      case TournamentType.AsyncTournament:
        return getAsyncTournamentPlaces(ranksCount);
      default:
        return [];
    }
  }, [teamsCount, isFreeTournament, tournamentType, maxTeamsCount, ranksCount]);

  return { places };
};
